import React, { useState, useRef } from 'react';
import {
  Box, Container, Typography, TextField, InputAdornment, IconButton,
  Accordion, AccordionSummary, AccordionDetails, Button, Paper, Grid,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FeedbackModal from '../components/FeedbackModal';
import SiteHeader from '../components/layout/SiteHeader';

/* ─── Data ─────────────────────────────────────────────────────────────── */

const BOOKING_STEPS = [
  { n: 1, icon: '🔍', title: 'Search', desc: 'Enter your destination and check-in date. The platform searches all available yachts for your dates.' },
  { n: 2, icon: '🎛️', title: 'Browse & Filter', desc: 'Narrow down by type, cabins, price, length, equipment and more. Use the map to filter by location.' },
  { n: 3, icon: '⛵', title: 'Choose a Yacht', desc: 'Open the detail page to review full specs, equipment list, availability calendar and a complete price breakdown.' },
  { n: 4, icon: '📋', title: 'Send a Request', desc: 'Fill in your name and email — no payment required at this stage. Add optional notes for the team.' },
  { n: 5, icon: '✅', title: 'Get Confirmed', desc: 'Our team contacts you within 24 hours to finalise. You also receive a confirmation email with your Sailing Plan link.' },
];

const CATEGORIES = [
  { id: 'searching',  icon: '🔍', title: 'Searching for a Yacht',     subtitle: 'Filters, map, sorting and more' },
  { id: 'details',   icon: '⛵', title: 'Yacht Details',               subtitle: 'Specs, availability and pricing' },
  { id: 'booking',   icon: '📋', title: 'Making a Booking',            subtitle: 'How to send a booking request' },
  { id: 'comparing', icon: '⚖️', title: 'Comparing Yachts',           subtitle: 'Side-by-side comparison with priority scoring' },
  { id: 'plans',     icon: '🗺️', title: 'Sailing Plans',              subtitle: 'Your shareable trip itinerary' },
  { id: 'pricing',   icon: '💶', title: 'Pricing & Extras',            subtitle: 'Costs, deposits and services' },
  { id: 'contact',   icon: '💬', title: 'Contact & Support',           subtitle: 'How to reach us' },
];

interface FaqItem { q: string; a: string; }
interface FaqCategory { id: string; title: string; items: FaqItem[]; }

const FAQ_DATA: FaqCategory[] = [
  {
    id: 'searching',
    title: 'Searching for a Yacht',
    items: [
      { q: 'How do I start a search?', a: 'Type a destination (e.g. "Greece" or "Lefkas") in the search bar on the home page and choose your check-in date. You will be taken to the search results page showing all available yachts.' },
      { q: 'What filters are available?', a: 'You can filter by Boat Type, Cabins, Bathrooms, Price range, Year range, Length range, Boat Model (with autocomplete), and Equipment. Open "All Filters" on mobile or use the sidebar on desktop.' },
      { q: 'How do I sort the results?', a: 'Use the "Order By" option inside the filters panel. You can sort by Lowest price, Lowest cabins, Highest discount, or Increasing length.' },
      { q: 'How does the map work?', a: 'An interactive map shows where all the yachts are based. Click a cluster on the map to filter results to that specific area. A "Map filter active" badge appears — click it to clear the map filter.' },
      { q: 'Can I search by a specific yacht model?', a: 'Yes. Use the "Boat Model" filter which has autocomplete suggestions. Start typing the model name (e.g. "Beneteau" or "Jeanneau") and select from the list.' },
      { q: 'How do I clear all filters?', a: 'When filters are active, a "Remove Filters" link appears inline with the filter chips on desktop. On mobile, open the filters drawer — a summary of active filters appears with a clear option.' },
      { q: 'How many results are shown per page?', a: 'Results are paginated. Use the page controls at the bottom of the search results to navigate between pages.' },
    ],
  },
  {
    id: 'details',
    title: 'Yacht Details & What\'s Included',
    items: [
      { q: 'What information is on the yacht detail page?', a: 'You will find full technical specs (year, cabins, berths, bathrooms, length, engine, water and fuel capacity), a complete equipment list, an availability calendar showing the yacht\'s weekly status, all pricing information, and any mandatory or optional services.' },
      { q: 'What does the availability calendar show?', a: 'The calendar shows each week\'s status: Available (you can book), Reserved (already booked), or On Option (temporarily held). Click a week to get the specific price for those dates.' },
      { q: 'What does "On Option" mean?', a: '"On Option" means another customer is currently in the process of confirming that week. The yacht is temporarily held for them. You can still submit a request and our team will add you to a waiting list in case the option falls through.' },
      { q: 'What is included in the yacht rental?', a: 'The weekly price covers use of the yacht only. Fuel, marina/mooring fees, food, provisioning, and any optional extras (skipper, hostess, bed linen, etc.) are not included unless stated as mandatory services.' },
      { q: 'What are mandatory services?', a: 'These are required add-ons you must pay when booking — typically a refundable security deposit and sometimes a skipper fee or cleaning fee. They are clearly listed on the yacht detail page and shown again in the booking form before you submit.' },
      { q: 'What is the security deposit?', a: 'A refundable security deposit is held during the charter to cover any potential damage. The amount varies by yacht. It is returned in full at the end of the trip provided no damage occurred.' },
      { q: 'Is a skipper available?', a: 'Some yachts require a mandatory skipper (included in the price or as a mandatory service). Others offer an optional skipper for an additional fee. A few yachts are bareboat only (no skipper). This is shown in the services section of each yacht.' },
      { q: 'What does the "One Way" badge mean?', a: 'A "One Way" charter means the departure base and the return base are different. You start in one port and end in another. The specific bases are shown on the yacht detail page.' },
    ],
  },
  {
    id: 'booking',
    title: 'Making a Booking',
    items: [
      { q: 'How do I make a booking?', a: 'From a yacht detail page, click the booking button to open the reservation form. Enter your name (required), email (required), phone number (optional) and any notes. Complete the security check and click "Send Request".' },
      { q: 'Do I need to pay anything immediately?', a: 'No. Submitting a booking request requires no payment. You are simply sending an enquiry. Our team will contact you within 24 hours to discuss availability and arrange payment.' },
      { q: 'What happens after I submit a booking request?', a: 'You receive a confirmation email at the address you provided, and our team will reach out within 24 hours to finalise the booking. You will also get a link to your Sailing Plan page.' },
      { q: 'What is the security check (CAPTCHA)?', a: 'We use Cloudflare Turnstile to verify that booking requests come from real people. The check appears automatically — most users will just see a green tick without needing to do anything.' },
      { q: 'Can I add notes to my booking?', a: 'Yes. There is an optional "Notes" field in the booking form where you can mention any preferences, questions, or special requirements for the team.' },
      { q: 'Will I receive a confirmation email?', a: 'Yes. A confirmation email is sent automatically to the email address you provide. It includes the yacht details, dates, price, and a link to your personalised Sailing Plan.' },
    ],
  },
  {
    id: 'comparing',
    title: 'Comparing Yachts',
    items: [
      { q: 'How do I compare yachts?', a: 'On any search result card, click the "Compare" button. A bar appears at the bottom of the screen. Add up to 4 yachts, then click "Compare (N)" to open the full comparison page.' },
      { q: 'How many yachts can I compare at once?', a: 'You can compare between 2 and 4 yachts simultaneously. The Compare button stays disabled until at least 2 yachts are selected.' },
      { q: 'What information is shown in the comparison?', a: 'The comparison table shows: Type, Year, Length, Cabins, Bathrooms, Berths, Gen / AC / WM (Generator, Air Conditioning, Watermaker), Price per week, Price per cabin, Price per berth, Security deposit, Base, and Country.' },
      { q: 'What is the Gen / AC / WM row?', a: 'This row shows at a glance whether each yacht has a Generator, Air Conditioning, and Watermaker. A green ✓ means the equipment is present; a grey ✗ means it is not. These are the three most impactful comfort extras for a charter.' },
      { q: 'What are the priority sliders?', a: 'At the top of the comparison page, three linked sliders let you set how much you care about Comfort, Price, and Sailing performance — they always sum to 100%. As you adjust them, the yacht columns automatically reorder so the best match for your priorities appears first.' },
      { q: 'What does the match score percentage mean?', a: 'Below each yacht\'s name you will see a fuchsia progress bar and a percentage. This is the weighted match score based on your current slider settings — a higher percentage means that yacht scores better across your chosen priorities.' },
      { q: 'What are the trophy icons on the comparison page?', a: 'Trophy icons highlight the best value in each category — for example, the yacht with the lowest price, newest year, or longest length. It makes it easy to spot the winner for each attribute at a glance.' },
      { q: 'Can I share a comparison with someone?', a: 'Yes. The comparison page has a Share button that generates a unique link. Anyone with the link can view the same comparison without needing to select the yachts again.' },
    ],
  },
  {
    id: 'plans',
    title: 'Sailing Plans',
    items: [
      { q: 'What is a Sailing Plan?', a: 'After you submit a booking request, you receive a personalised Sailing Plan page. It shows the booked yacht\'s specs, equipment, and an AI-generated day-by-day sailing itinerary for your departure base and duration.' },
      { q: 'How is the sailing itinerary generated?', a: 'Our AI creates a realistic route based on your departure base and number of sailing days, using actual harbours and anchorages in the region. The itinerary shows each day\'s from/to legs, nautical miles, and a highlight of the destination.' },
      { q: 'Can I share my Sailing Plan with my crew?', a: 'Yes. Use the "Share with your crew" button on the Sailing Plan page to copy a unique link. When shared on WhatsApp or other messaging apps, the link generates a preview card with the yacht photo and dates.' },
      { q: 'How long is the Sailing Plan available?', a: 'The Sailing Plan link is valid until the start date of your booking. Once the trip begins, the link expires — at that point you are already on the boat!' },
    ],
  },
  {
    id: 'pricing',
    title: 'Pricing & Extras',
    items: [
      { q: 'Are prices shown per week?', a: 'Yes. All yacht prices on the platform are displayed per week (Saturday to Saturday charter schedule). The exact dates and total price are confirmed in the booking summary.' },
      { q: 'What does a discounted price look like?', a: 'When a yacht has a discount, the original price is shown with a strikethrough and the discounted price is highlighted. You can sort search results by "Highest discount" to find the best deals.' },
      { q: 'What is not included in the price?', a: 'Fuel, marina and mooring fees, food and provisioning, tourist taxes, and optional extras (skipper, hostess, bed linen, towels, etc.) are typically not included. Always check the mandatory services section on each yacht page.' },
      { q: 'What currency are prices shown in?', a: 'All prices are shown in EUR (Euro). The actual payment currency and method will be agreed with our team during the booking confirmation process.' },
      { q: 'Why do prices differ between similar yachts?', a: 'Prices vary based on yacht age, size, equipment level, base location, and season. Newer, larger, or better-equipped yachts in popular destinations tend to be priced higher. Use the price range filter and "Price per cabin" comparison to find the best value.' },
    ],
  },
  {
    id: 'contact',
    title: 'Contact & Support',
    items: [
      { q: 'How can I contact SailArmada?', a: 'You can reach us on WhatsApp at +40 744 276 232, by email at hello@sailarmada.com, or via the Contact page. For quick questions, WhatsApp is the fastest.' },
      { q: 'How quickly will I get a response?', a: 'Booking requests are confirmed within 24 hours. WhatsApp messages are typically answered within a few hours during business hours.' },
      { q: 'How do I report a bug or leave feedback?', a: 'Use the "Feedback" button on the left side of any page. You can select Bug Report, Suggestion, or General Feedback and describe what you experienced. We read everything.' },
      { q: 'I found a problem with a listing — what should I do?', a: 'Use the Feedback button to send us a Bug Report with the yacht name and what seemed incorrect. Include the page URL (it is pre-filled automatically) so we can investigate quickly.' },
    ],
  },
];

/* ─── Component ─────────────────────────────────────────────────────────── */

const HelpPage: React.FC = () => {
  const [query, setQuery] = useState('');
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const trimmed = query.trim().toLowerCase();
  const filtered: FaqCategory[] = trimmed
    ? FAQ_DATA.map(cat => ({
        ...cat,
        items: cat.items.filter(i =>
          i.q.toLowerCase().includes(trimmed) || i.a.toLowerCase().includes(trimmed)
        ),
      })).filter(cat => cat.items.length > 0)
    : FAQ_DATA;

  const scrollTo = (id: string) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box sx={{ minHeight: '100vh', background: '#f8f9fa' }}>
      <SiteHeader />

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)',
        pt: 'calc(var(--banner-height, 0px) + 100px)',
        pb: 8,
        px: 2,
        textAlign: 'center',
      }}>
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800, mb: 1, fontFamily: "'Montserrat', sans-serif" }}>
          How can we help you?
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 4, fontSize: '1rem' }}>
          Guides, answers, and everything you need to plan your sailing trip.
        </Typography>

        <Box sx={{ maxWidth: 560, mx: 'auto' }}>
          <TextField
            fullWidth
            placeholder="Search for help..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#888' }} />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setQuery('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: { background: '#fff', borderRadius: 2, fontSize: '1rem' },
            }}
            sx={{ '& fieldset': { border: 'none' }, boxShadow: '0 4px 24px rgba(0,0,0,0.18)', borderRadius: 2 }}
          />
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>

        {/* ── Category Cards ─────────────────────────────────────────────── */}
        {!trimmed && (
          <Box sx={{ mb: 7 }}>
            <Grid container spacing={2.5}>
              {CATEGORIES.map(cat => (
                <Grid item xs={12} sm={6} md={4} key={cat.id}>
                  <Paper
                    elevation={0}
                    onClick={() => scrollTo(cat.id)}
                    sx={{
                      p: 3, borderRadius: 2, cursor: 'pointer',
                      border: '1px solid #e8eaed',
                      transition: 'box-shadow 0.2s ease, transform 0.15s ease',
                      '&:hover': { boxShadow: '0 6px 24px rgba(0,0,0,0.1)', transform: 'translateY(-2px)' },
                    }}
                  >
                    <Typography sx={{ fontSize: '2rem', mb: 1 }}>{cat.icon}</Typography>
                    <Typography sx={{ fontWeight: 700, mb: 0.5, fontFamily: "'Montserrat', sans-serif", fontSize: '0.95rem' }}>
                      {cat.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{cat.subtitle}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* ── How to Book Stepper ────────────────────────────────────────── */}
        {!trimmed && (
          <Box sx={{ mb: 8 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 4, fontFamily: "'Montserrat', sans-serif", textAlign: 'center' }}>
              How to Book in 5 Steps
            </Typography>

            {/* Desktop: horizontal */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'flex-start', gap: 0 }}>
              {BOOKING_STEPS.map((step, idx) => (
                <Box key={step.n} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                  {/* connector line */}
                  {idx < BOOKING_STEPS.length - 1 && (
                    <Box sx={{
                      position: 'absolute', top: 22, left: '50%', width: '100%',
                      height: 2, background: '#e0e0e0', zIndex: 0,
                    }} />
                  )}
                  {/* circle */}
                  <Box sx={{
                    width: 44, height: 44, borderRadius: '50%',
                    background: '#1a1a2e', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '1rem', mb: 1.5, zIndex: 1,
                    position: 'relative',
                  }}>
                    {step.n}
                  </Box>
                  <Typography sx={{ fontSize: '1.4rem', mb: 0.75 }}>{step.icon}</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', mb: 0.75, fontFamily: "'Montserrat', sans-serif", textAlign: 'center' }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', px: 1, lineHeight: 1.5 }}>
                    {step.desc}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Mobile: vertical */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 0 }}>
              {BOOKING_STEPS.map((step, idx) => (
                <Box key={step.n} sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: '#1a1a2e', color: '#fff', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: '0.9rem',
                    }}>
                      {step.n}
                    </Box>
                    {idx < BOOKING_STEPS.length - 1 && (
                      <Box sx={{ width: 2, flex: 1, background: '#e0e0e0', my: 0.5 }} />
                    )}
                  </Box>
                  <Box sx={{ pb: 3 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', fontFamily: "'Montserrat', sans-serif" }}>
                      {step.icon} {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                      {step.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ── FAQ Sections ───────────────────────────────────────────────── */}
        <Box sx={{ mb: 8 }}>
          {trimmed && filtered.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8, color: '#888' }}>
              <Typography sx={{ fontSize: '2rem', mb: 2 }}>🔍</Typography>
              <Typography variant="body1">
                No results for "<strong>{query}</strong>" — try different keywords or{' '}
                <Box component="span"
                  onClick={() => setIsFeedbackOpen(true)}
                  sx={{ color: '#E8368A', cursor: 'pointer', textDecoration: 'underline' }}>
                  contact us
                </Box>.
              </Typography>
            </Box>
          )}

          {filtered.map(cat => (
            <Box
              key={cat.id}
              ref={(el: HTMLDivElement | null) => { sectionRefs.current[cat.id] = el; }}
              sx={{ mb: 5, scrollMarginTop: 'calc(var(--banner-height, 0px) + 80px)' }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 2.5, fontFamily: "'Montserrat', sans-serif", color: '#1a1a2e' }}>
                {CATEGORIES.find(c => c.id === cat.id)?.icon} {cat.title}
              </Typography>

              <Box>
                {cat.items.map((item, i) => (
                  <Accordion key={i} elevation={0} disableGutters
                    sx={{
                      border: '1px solid #e8eaed',
                      borderRadius: '8px !important',
                      mb: 1,
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': { boxShadow: '0 2px 12px rgba(0,0,0,0.07)' },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}
                      sx={{ px: 2.5, py: 0.5, '& .MuiAccordionSummary-content': { my: 1.5 } }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.4 }}>
                        {item.q}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                      <Divider sx={{ mb: 1.5 }} />
                      <Typography variant="body2" sx={{ lineHeight: 1.8, color: '#444' }}>
                        {item.a}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        {/* ── Still Need Help ────────────────────────────────────────────── */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          borderRadius: 3, p: { xs: 4, md: 6 },
          textAlign: 'center', color: '#fff',
        }}>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, fontFamily: "'Montserrat', sans-serif" }}>
            Still have questions?
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 4, maxWidth: 420, mx: 'auto' }}>
            Our team is happy to help. Reach us on WhatsApp for a quick reply, or send a message and we'll get back to you within 24 hours.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              href="https://wa.me/40744276232"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                background: '#25D366', '&:hover': { background: '#1ebe5d' },
                fontWeight: 700, px: 3, py: 1.5, borderRadius: 2,
              }}
            >
              💬 Chat on WhatsApp
            </Button>
            <Button
              variant="outlined"
              onClick={() => setIsFeedbackOpen(true)}
              sx={{
                color: '#fff', borderColor: 'rgba(255,255,255,0.4)',
                '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.08)' },
                fontWeight: 700, px: 3, py: 1.5, borderRadius: 2,
              }}
            >
              ✉️ Send us a message
            </Button>
          </Box>
        </Box>

      </Container>

      <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
    </Box>
  );
};

export default HelpPage;
